import { isValidElement, ReactNode, useEffect } from "react";
import { useImmer } from "use-immer";
import * as UUID from "uuid";

import { cx } from "utils";


type ContentLayer = {
  id: string;
  content: ReactNode;
  visible: boolean;
};

export default function CrossFadeContainer(
  {
    duration = 300,
    delay = 100,
    children
  }: {
    duration?: number,
    delay?: number,
    children: ReactNode
  }
) {
  const [ contentLayers, setContentLayers ] = useImmer<ContentLayer[]>([ { id: UUID.v7(), content: children, visible: true } ]);

  useEffect(() => {
    // do not add the same content again
    const shouldAddNewLayer = (() => {
      if (contentLayers.length > 0) {
        if (contentLayers[0].content === children) {
          return false;
        }
        if (
          isValidElement(contentLayers[0].content) && contentLayers[0].content.key &&
          isValidElement(children) && children.key &&
          contentLayers[0].content.key === children.key
        ) {
          return false;
        }
      }

      return true;
    })();

    if (shouldAddNewLayer) {
      // add the new layer with visible:false, so later we can trigger a fade-in animation
      setContentLayers(layers => { layers.unshift({ id: UUID.v7(), content: children, visible: false }); });
    }

    // trigger fade-in animation immediately
    const fadeInTimeoutHandle = setTimeout(() => {
      setContentLayers(layers => { layers[0].visible = true; });
    }, 0);

    // remove previous layers after fade animation is completed
    const timeoutHandle = setTimeout(() => {
      if (contentLayers.length > 1) {
        setContentLayers(layers => { layers.splice(1) });
      }
    }, duration + delay);

    return () => {
      clearTimeout(fadeInTimeoutHandle);
      clearTimeout(timeoutHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ children ]);


  return (
    <div className="relative">
      { contentLayers.map((layer, idx) => (
        <div
          key={ layer.id }
          className={ cx(
            "transition-opacity ease-in-out",
            (idx !== 0) && "absolute inset-0",  // render additional layers as absolute, so the parent will be resized properly for the new content
            (layer.visible && (idx === 0)) ? "opacity-100" : "opacity-0 pointer-events-none"
          ) }
          style={ { transitionDuration: `${duration}ms`, transitionDelay: `${delay}ms` } }
        >
          { layer.content }
        </div>
      )) }
    </div>
  );
};
